import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../../store";
import {
  Employee,
  MessageType,
  MessageThread,
  PersonaExpanded,
} from "winslow-core";

import { AgentResponseMessage } from "../../../components/agent-chat/types";
export type AgentState = {
  messages: AgentResponseMessage[];
  employees: Employee[];
  personas: PersonaExpanded[];
  currentThread: MessageThread | null;
  threads: MessageThread[];
  prompt: string;
  hasMoreMessages: boolean;
  isLoadingMore: boolean;
  isInitialLoading: boolean;
  totalMessageCount: number;
};

//state to be used by other link preview components
const initialState = {
  messages: [],
  employees: [],
  personas: [],
  currentThread: null,
  threads: [],
  prompt: "",
  hasMoreMessages: true,
  isLoadingMore: false,
  isInitialLoading: true,
  totalMessageCount: 0,
} as AgentState;

export const agentSlice = createSlice({
  name: "agent",
  initialState,
  reducers: {
    setPrompt: (state: AgentState, action: PayloadAction<string>) => {
      state.prompt = action.payload;
    },
    setMessages: (
      state: AgentState,
      action: PayloadAction<AgentResponseMessage[]>
    ) => {
      console.log("setMessages", action.payload);
      state.messages = action.payload;
      state.isInitialLoading = false; // Set to false when messages are loaded
      if (action.payload.length > 0) {
        const threads = action.payload.map((message) => message.thread);
        const uniqueThreads = threads.filter(
          (thread, index, self) =>
            index === self.findIndex((t) => t?.id === thread?.id)
        );
        state.threads = uniqueThreads as MessageThread[];
        state.currentThread = uniqueThreads[
          uniqueThreads.length - 1
        ] as MessageThread; //newest thread
      }
    },
    addMessage: (
      state: AgentState,
      action: PayloadAction<AgentResponseMessage>
    ) => {
      state.messages = [...state.messages, action.payload];
      if (action.payload.type === MessageType.NEW_THREAD) {
        state.currentThread = action.payload.thread;
        state.threads = [...state.threads, action.payload.thread];
      }
    },
    prependMessages: (
      state: AgentState,
      action: PayloadAction<{
        messages: AgentResponseMessage[];
        hasMore: boolean;
        totalCount: number;
      }>
    ) => {
      // Prepend older messages to the beginning of the array
      const existingMessageIds = new Set(state.messages.map((m) => m.id));
      const newMessages = action.payload.messages.filter(
        (m) => !existingMessageIds.has(m.id)
      );
      state.messages = [...newMessages, ...state.messages];
      state.hasMoreMessages = action.payload.hasMore;
      state.totalMessageCount = action.payload.totalCount;
      state.isLoadingMore = false;
    },
    setLoadingMore: (state: AgentState, action: PayloadAction<boolean>) => {
      state.isLoadingMore = action.payload;
    },
    setInitialLoading: (state: AgentState, action: PayloadAction<boolean>) => {
      state.isInitialLoading = action.payload;
    },
    setPaginationData: (
      state: AgentState,
      action: PayloadAction<{
        hasMore: boolean;
        totalCount: number;
      }>
    ) => {
      state.hasMoreMessages = action.payload.hasMore;
      state.totalMessageCount = action.payload.totalCount;
    },
    setEmployees: (state: AgentState, action: PayloadAction<Employee[]>) => {
      state.employees = action.payload;
    },
    setPersonas: (
      state: AgentState,
      action: PayloadAction<PersonaExpanded[]>
    ) => {
      state.personas = action.payload;
    },
    setCurrentThread: (
      state: AgentState,
      action: PayloadAction<MessageThread | null>
    ) => {
      state.currentThread = action.payload;
    },
    setThreads: (state: AgentState, action: PayloadAction<MessageThread[]>) => {
      state.threads = action.payload;
    },
  },
});

export const {
  setMessages,
  setEmployees,
  setPersonas,
  addMessage,
  setCurrentThread,
  setPrompt,
  prependMessages,
  setLoadingMore,
  setInitialLoading,
  setPaginationData,
} = agentSlice.actions;

export const selectMessages = (state: RootState) => state.agent.messages;
export const selectEmployees = (state: RootState) => state.agent.employees;
export const selectPersonas = (state: RootState) => state.agent.personas;
export const selectCurrentThread = (state: RootState) =>
  state.agent.currentThread;
export const selectThreads = (state: RootState) => state.agent.threads;
export const selectPrompt = (state: RootState) => state.agent.prompt;
export const selectHasMoreMessages = (state: RootState) =>
  state.agent.hasMoreMessages;
export const selectIsLoadingMore = (state: RootState) =>
  state.agent.isLoadingMore;
export const selectIsInitialLoading = (state: RootState) =>
  state.agent.isInitialLoading;
export const selectTotalMessageCount = (state: RootState) =>
  state.agent.totalMessageCount;
export default agentSlice.reducer;
